
import { useTranslation } from "react-i18next";
import React, { useEffect, useRef } from "react";
import Webcam from "react-webcam";
import { Button, CircularProgress, Progress } from "@nextui-org/react";
import { StopRecordIcon } from "../../icons/StopRecordIcon";
import { RecordIcon3 } from "../../icons/RecordIcon3";
import { RedoIcon } from "../../icons/RedoIcon";
import { DoneIcon } from "../../icons/DoneIcon";
import { PlayIcon } from "../../icons/PlayIcon";
import { PauseIcon } from "../../icons/PauseIcon";
import { IS_IOS } from "../../utils";
import { trackEvent } from "../../../utils/analytics";

const VideoAgreementCamera = ({ script, onCapture, uploading, flowId, verificationId }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = React.useState(0);
  const webcamRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const [capturing, setCapturing] = React.useState(false);
  const [recordedChunks, setRecordedChunks] = React.useState([]);
  const [captured, setCaptured] = React.useState(false);
  const videoRef = useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [videoSrc, setVideoSrc] = React.useState("");
  const [cameraReady, setCameraReady] = React.useState(false);
  const [cameraError, setCameraError] = React.useState(null);
  const [webcamKey, setWebcamKey] = React.useState(0);
  const retryTimeoutRef = useRef(null);

  // Auto-retry when camera fails to start — mirrors DocumentCamera.jsx.
  useEffect(() => {
    if (!cameraError) return;
    retryTimeoutRef.current = setTimeout(() => {
      setCameraReady(false);
      setCameraError(null);
      setWebcamKey((k) => k + 1);
    }, 1500);
    return () => clearTimeout(retryTimeoutRef.current);
  }, [cameraError]);

  // The camera can be interrupted by backgrounding the app (lock screen, app
  // switch): a dead/muted track leaves a frozen feed, and on iOS (Safari and
  // every other iOS browser, since they all run on WebKit) the video element
  // can paint a stale/blank frame on resume even while the track itself still
  // reports readyState "live" and muted: false. Mirrors the fix in
  // DocumentCamera.jsx.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (captured || recordedChunks.length) return;
      const track = webcamRef.current?.stream?.getVideoTracks?.()?.[0];
      const trackUnhealthy = track && (track.readyState !== "live" || track.muted);
      if (trackUnhealthy || IS_IOS) {
        setCameraError("Camera interrupted");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captured, recordedChunks.length]);

  const videoConstraints = {
    facingMode: "user",
    width: 1280,
    height: 720,
  };

  const totalTime = 10000; // total time in milliseconds to reach 100%
  const updateInterval = 100; // update every 100 milliseconds
  const incrementValue = 100 / (totalTime / updateInterval); // calculate increment per update
  const intervalRef = React.useRef(null); // Store interval ID

  const handleStopEvent = React.useCallback(() => {
    // Using a state setter function to log or utilize the latest state
    setRecordedChunks((currentChunks) => {
      const urlSrc = getRecordedChunkSrc(currentChunks); // Modify getRecordedChunkSrc to accept chunks as parameter
      setVideoSrc(urlSrc);
      return currentChunks; // Return the unchanged chunks
    });
  }, [setRecordedChunks, setVideoSrc]);

  const handleStopCaptureClick = React.useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current); // Clear progress interval
      intervalRef.current = null;
    }
    mediaRecorderRef.current.stop();
    setCapturing(false);
    setCaptured(true);
    setPlaying(true);
  }, [mediaRecorderRef, setCapturing]);

  const handleVideoEnd = () => {
    setPlaying(false);
  };

  const handleDataAvailable = React.useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data));
      }
    },
    [setRecordedChunks]
  );

  const handleStartCaptureClick = React.useCallback(() => {
    trackEvent("video_agreement_recording_started", { flowId, verificationId });
    setCapturing(true);
    setCaptured(false);
    setProgress(0); // Reset progress when starting

    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/mp4",
    });

    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    );
    mediaRecorderRef.current.addEventListener("stop", handleStopEvent);
    mediaRecorderRef.current.start();

    intervalRef.current = setInterval(() => {
      setProgress((currentProgress) => {
        const newProgress = currentProgress + incrementValue;
        if (newProgress >= 100) {
          handleStopCaptureClick(); // Stop recording
          return 100;
        }
        return newProgress;
      });
    }, updateInterval);
  }, [
    webcamRef,
    setCapturing,
    mediaRecorderRef,
    handleStopCaptureClick,
    handleDataAvailable,
    handleStopEvent,
    incrementValue,
    flowId,
    verificationId,
  ]);

  const handleProceed = React.useCallback(() => {
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, { type: "video/mp4" });
      onCapture({
        type: "video",
        value: blob,
      });
    }
  }, [onCapture, recordedChunks]);

  const recapture = () => {
    setCaptured(false);
    setRecordedChunks([]);
  };

  const handleVideoPlayerCtrl = () => {
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setPlaying(!playing);
  };

  const getRecordedChunkSrc = (currentChunks) => {
    if (currentChunks.length) {
      const blob = new Blob(currentChunks, { type: "video/mp4" });
      return URL.createObjectURL(blob);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex flex-col h-full items-center">
        {!captured && !recordedChunks.length && (
          <Webcam
            key={webcamKey}
            audio
            muted={true}
            ref={webcamRef}
            mirrored={true}
            imageSmoothing={false}
            videoConstraints={videoConstraints}
            onUserMedia={(mediaStream) => {
              setCameraReady(true);
              setCameraError(null);
              const track = mediaStream?.getVideoTracks?.()?.[0];
              if (track) {
                track.onmute = () => setCameraError("Camera interrupted");
                track.onended = () => setCameraError("Camera interrupted");
              }
            }}
            onUserMediaError={(err) => {
              console.error("Camera error:", err);
              setCameraError(err?.message || "Camera unavailable");
            }}
          />
        )}

        {/* Loading/retry overlay — camera stream not yet live */}
        {!captured && !recordedChunks.length && (!cameraReady || cameraError) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 gap-3">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
            <span className="text-white text-sm font-medium">
              {cameraError ? "Retrying camera…" : "Starting camera…"}
            </span>
          </div>
        )}

        {recordedChunks.length > 0 && captured && (
          <div className="w-full h-full">
            <video
              autoPlay
              ref={videoRef}
              src={videoSrc}
              playsInline
              onEnded={handleVideoEnd}
              style={{
                transform: "scaleX(-1)", // mirror to match live feed
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        {recordedChunks.length > 0 && captured && uploading && (
          <div className="flex flex-col p-2 gap-4 absolute top-0 left-0 right-0 bottom-0 justify-end mb-10 items-center">
            <div className="text-center p-4">
              <div className="text-xl font-bold text-white">
                {t("videoAgreement.camera.uploadingTitle")}
              </div>
              <div className="text-sm text-white">
                {t("videoAgreement.camera.uploadingWarning")}
              </div>
            </div>
            <div className="flex flex-col w-full gap-2 p-4">
              <Progress
                size="sm"
                isIndeterminate
                aria-label="Loading..."
                className="max-w-md text-white"
              />
              <span className="text-sm text-white">{t("videoAgreement.camera.uploadingLabel")}</span>
            </div>
          </div>
        )}

        {captured && (
          <div className="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-between p-2">
            <div className="flex-grow flex items-center justify-center">
              {/* Centered Play/Pause Button */}
              <Button
                isIconOnly
                variant="light"
                radius="sm"
                size="sm"
                className="text-white"
                isDisabled={!videoSrc}
                onClick={handleVideoPlayerCtrl}
              >
                {playing ? (
                  <PauseIcon height={100} width={100} />
                ) : (
                  <PlayIcon height={100} width={100} />
                )}
              </Button>
            </div>

            {!uploading && (
              <div className="flex flex-col p-2 mb-12 items-center text-center">
                <div className="text-xl font-bold text-white">
                  {t("videoAgreement.camera.reviewTitle")}
                </div>
                <div className="text-sm text-white">
                  {t("videoAgreement.camera.reviewDesc")}
                </div>
              </div>
            )}

            {/* Retake and Proceed Buttons at the Bottom */}
            {!uploading && (
              <div className="flex flex-col gap-4">
                <div className="desc text-center">
                  {t("videoAgreement.camera.agreementNotice")}
                </div>
                <div className="flex justify-center gap-4">
                  <Button
                    variant="light"
                    radius="sm"
                    size="sm"
                    className="text-white"
                    startContent={<RedoIcon height={20} width={20} />}
                    onClick={recapture}
                  >
                    {t("videoAgreement.camera.retake")}
                  </Button>
                  <Button
                    color="primary"
                    radius="sm"
                    size="sm"
                    startContent={<DoneIcon height={20} width={20} />}
                    onClick={handleProceed}
                  >
                    {t("videoAgreement.camera.upload")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {capturing && (
          <div className="absolute top-0 left-0 right-0 bottom-0 flex justify-center items-end">
            <div
              style={{ backgroundColor: "#0B1638" }}
              className="flex flex-col gap-6 p-4 w-full flex justify-center items-center"
            >
              <div className="text-md text-white text-center">{script}</div>
              <CircularProgress
                aria-label="Loading..."
                size="lg"
                value={progress}
                valueLabel={
                  <div onClick={handleStopCaptureClick}>
                    <StopRecordIcon height={20} width={20} />
                  </div>
                }
                color="primary"
                showValueLabel={true}
              />
            </div>
          </div>
        )}

        {!capturing && recordedChunks.length < 1 && cameraReady && (
          <div className="p-2 absolute top-0 left-0 right-0 bottom-0 flex justify-center items-end">
            <Button
              radius="sm"
              startContent={<RecordIcon3 />}
              onClick={handleStartCaptureClick}
              className="w-full"
              color="primary"
            >
              {t("videoAgreement.camera.startRecording")}
            </Button>
          </div>
        )}

        {!capturing && recordedChunks.length < 1 && cameraReady && (
          <div className="flex flex-col p-2 absolute top-0 left-0 right-0 bottom-0 justify-end mb-28 items-center">
            <div className="text-md font-bold text-white">
              {t("videoAgreement.camera.tapToStart")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoAgreementCamera;
